import { createFileRoute } from "@tanstack/react-router";
import { Fragment } from "react";
import { ChartLine, Globe, Lock, Target } from "@phosphor-icons/react";

import { PageHeader } from "@/components/layout/heading";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { GrowthTrend } from "@/components/brand-intelligence/charts/growth-trend";
import { SectionHeader } from "@/components/brand-intelligence/layout/heading";

import { useSession } from "@/hooks/use-session";
import { mEnterpriseBrandIntelligence } from "@/mock/brand-intelligence";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/dashboard/research/_layout/$id/social")({
  component: RouteComponent,
});

const mMaxRate = 10;

function getPlatformBarColor(platform: string) {
  const _platform = platform.toLowerCase();

  if (_platform.includes("linkedin")) return "bg-chart-3";
  if (_platform.includes("tiktok")) return "bg-chart-2";
  if (_platform.includes("instagram")) return "bg-chart-1";
  if (_platform.includes("youtube")) return "bg-chart-5";
  if (_platform.includes("facebook")) return "bg-chart-4";
  if (_platform.includes("twitter/x")) return "bg-chart-5";
  return "bg-muted-foreground";
}

function RouteComponent() {
  return (
    <Fragment>
      <SectionHeader title="Social & Digital" icon={<Globe className="text-sky-500 size-6" />} iconClassName="bg-sky-500/10" />
      <div className="space-y-6">
        <PlatformOverview />
        <GrowthTrend />
        <PerformanceSummary />
        <AICommentsModeration />
      </div>
    </Fragment>
  );
}

function PlatformOverview() {
  const session = useSession();

  const brandIntelligence = mEnterpriseBrandIntelligence[session.user.email];
  const socialPlatformMetrics = brandIntelligence.social_and_digital.platform_metrics;
  const platformsWithFollowers = socialPlatformMetrics
    .filter((item) => item.followers !== 0)
    .map((item) => ({
      ...item,
      color: getPlatformBarColor(item.platform),
    }));

  return (
    <Fragment>
      <PageHeader title="Platform Overview" description="Social presence, followers, growth trends, and platform health scores" titleClassName="text-base" />
      <div className="w-full grid grid-flow-col gap-6 mt-6 overflow-x-auto scrollbar-hidden">
        {platformsWithFollowers.map((item) => (
          <Card key={item.platform} className="min-w-80">
            <CardHeader>
              <CardTitle>{item.platform}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-y-3.5 h-full">
              <div>
                <p className="text-2xl font-bold">{Intl.NumberFormat("en-US", { notation: "compact", compactDisplay: "short" }).format(item.followers)}</p>
                <p className="text-sm text-muted-foreground">followers</p>
              </div>
              <div className="space-y-1.5 mt-auto">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Health Score</span>
                  <span className="font-medium">{Intl.NumberFormat("en-US", { style: "percent" }).format(item.health_score / 100)}</span>
                </div>
                <Progress value={item.health_score} className="h-1.5 bg-muted" indicatorClassName={item.color} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </Fragment>
  );
}

function PerformanceSummary() {
  const session = useSession();

  const brandIntelligence = mEnterpriseBrandIntelligence[session.user.email];
  const social = brandIntelligence.social_and_digital;
  const engagementByPlatform = social.engagement.engagement_by_platform;
  const bestPerformingContentTypes = social.engagement.best_performing_content_types;
  const platformsWithEngagement = engagementByPlatform.filter((item) => item.engagement_rate !== 0);
  const bestPerformingContentTypesWithPerformance = bestPerformingContentTypes.filter((item) => item.performance !== 0);

  return (
    <Fragment>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center gap-2">
            <ChartLine className="size-5 text-orange-600" />
            <CardTitle>Engagement Rate by Platform</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-5">
            {platformsWithEngagement.map((item) => (
              <div key={item.platform} className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-foreground">{item.platform}</p>
                  <span className="text-sm font-medium text-foreground">
                    {Intl.NumberFormat("en-US", { style: "percent", maximumFractionDigits: 2 }).format((item.engagement_rate * mMaxRate) / 100)}
                  </span>
                </div>
                <Progress value={item.engagement_rate * mMaxRate} className="h-1.5 bg-muted" indicatorClassName="bg-chart-5 rounded-full" />
              </div>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center gap-2">
            <Target className="size-5 text-orange-600" />
            <CardTitle>Best Performing Content Types</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-5">
            {bestPerformingContentTypesWithPerformance.map((item) => (
              <div key={item.content_type} className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-foreground">{item.content_type}</p>
                  <span className="text-sm font-medium text-foreground">{Intl.NumberFormat("en-US", { style: "percent" }).format((item.performance * mMaxRate) / 100)}</span>
                </div>
                <Progress value={item.performance * mMaxRate} className="h-1.5 bg-muted" indicatorClassName="bg-chart-3 rounded-full" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </Fragment>
  );
}

function AICommentsModeration() {
  return (
    <Fragment>
      <div className="flex items-center justify-between mb-6">
        <div className="flex flex-col">
          <PageHeader title="AI Comment Moderation Dashboard" titleClassName="text-base" />
          <p className="text-sm text-muted-foreground">Real-time sentiment analysis and comment monitoring across platforms</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 min-h-72">
        <CommentsByPlatform />
        <SentimentDistribution />
        <RecentCommentsFeed />
      </div>
    </Fragment>
  );
}

function CommentsByPlatform() {
  const session = useSession();

  const brandIntelligence = mEnterpriseBrandIntelligence[session.user.email];
  const social = brandIntelligence.social_and_digital;
  const platformsWithEngagement = social.engagement.engagement_by_platform.filter((item) => item.engagement_rate !== 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Comments by Platform</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2.5">
        {platformsWithEngagement.map((item) => (
          <div key={item.platform} className="flex items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">{item.platform}</p>
            <span className="text-sm font-semibold text-foreground">{item.engagement_rate}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function SentimentDistribution() {
  const session = useSession();

  const brandIntelligence = mEnterpriseBrandIntelligence[session.user.email];
  const social = brandIntelligence.social_and_digital;
  const sentimentDistribution = social.sentiment_distribution.map((item) => ({
    ...item,
    color: item.label.toLowerCase() === "positive" ? "bg-success" : item.label.toLowerCase() === "neutral" ? "bg-muted-foreground" : "bg-destructive",
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sentiment Distribution</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2.5">
        {sentimentDistribution.map((item) => (
          <div key={item.label} className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <p className={["text-sm font-medium", item.label].join(" ")}>{item.label}</p>
              <span className="text-sm font-medium text-foreground">{Intl.NumberFormat("en-US", { style: "percent" }).format(item.percentage / 100)}</span>
            </div>
            <Progress value={item.percentage} className="h-1.5 bg-muted" indicatorClassName={cn("rounded-full", item.color)} />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function RecentCommentsFeed() {
  const session = useSession();

  const brandIntelligence = mEnterpriseBrandIntelligence[session.user.email];
  const social = brandIntelligence.social_and_digital;
  return (
    <Card className="col-span-1 md:col-span-2">
      <CardContent className="flex-1 relative overflow-hidden flex flex-col">
        <CardHeader>
          <CardTitle>Recent Comments Feed</CardTitle>
        </CardHeader>
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-background/90 pointer-events-none">
          <Lock className="size-12 text-muted-foreground" />
          <p className="text-base font-medium text-muted-foreground">Connect your social accounts</p>
        </div>
        <div className="space-y-3 overflow-hidden max-h-52">
          {social.recent_comments.map((comment, index) => (
            <div key={`${comment.user}-${index}`} className="rounded-lg border bg-muted/70 px-3 py-2.5 space-y-1">
              <div className="flex justify-between">
                <p className="text-sm font-semibold text-foreground">{comment.user}</p>
                <div className="flex items-center gap-1.5">
                  <p className="text-xs text-muted-foreground">
                    {comment.platform} • {comment.timeAgo}
                  </p>
                  <span
                    className={cn(
                      "size-1.5 shrink-0 rounded-full",
                      comment.status === "positive" && "bg-green-500",
                      comment.status === "neutral" && "bg-muted-foreground",
                      comment.status === "negative" && "bg-red-500"
                    )}
                    aria-hidden
                  />
                </div>
              </div>
              <p className="text-sm text-foreground">{comment.commentText}</p>
              <p className="text-xs text-muted-foreground">{comment.postContext}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
